import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const RefundPolicy = () => {
    return (
        <div style={styles.page}>
            <Helmet>
                <title>Refund Policy | APILoadTest.com</title>
                <meta name="description" content="Refund Policy for APILoadTest.com. Learn about our refund process for credit pack purchases." />
                <link rel="canonical" href="https://www.apiloadtest.com/refund" />
                <meta name="robots" content="index, follow" />
            </Helmet>

            {/* Header */}
            <header style={styles.header}>
                <div style={styles.headerContainer}>
                    <Link to="/" style={styles.logoSection}>
                        <img src="/logo.png" alt="APILoadTest.com" style={styles.logoImage} />
                        <span style={styles.logoText}>APILoadTest.com</span>
                    </Link>
                </div>
            </header>

            <main style={styles.content}>
                <h1 style={styles.title}>Refund Policy</h1>
                <p style={styles.effectiveDate}>Effective Date: February 25, 2026</p>

                <section style={styles.section}>
                    <h2 style={styles.heading}>1. Overview</h2>
                    <p style={styles.paragraph}>
                        APILoadTest.com ("the Service") operates on a pay-as-you-go credit pack model. This Refund Policy outlines the terms under which refunds may be issued for credit pack purchases.
                    </p>
                </section>

                <section style={styles.section}>
                    <h2 style={styles.heading}>2. Digital Product Nature</h2>
                    <p style={styles.paragraph}>
                        Credit packs are digital products that provide immediate access to load testing capabilities upon purchase. Because credits can be consumed immediately, refund eligibility depends on credit usage status.
                    </p>
                </section>

                <section style={styles.section}>
                    <h2 style={styles.heading}>3. Refund Eligibility</h2>
                    <h3 style={styles.subheading}>Eligible for Refund:</h3>
                    <ul style={styles.list}>
                        <li>Duplicate or accidental purchases — if you were charged more than once for the same credit pack</li>
                        <li>Technical issues — if a credit was consumed due to a platform error (e.g., test failed to execute but credit was deducted)</li>
                        <li>Unused credits — if no credits from the purchased pack have been consumed and the refund is requested within 7 days of purchase</li>
                    </ul>
                    <h3 style={styles.subheading}>Not Eligible for Refund:</h3>
                    <ul style={styles.list}>
                        <li>Credits that have been partially or fully consumed</li>
                        <li>Dissatisfaction with test results (results reflect your API's actual performance)</li>
                        <li>Failure to use credits (credits do not expire and remain available)</li>
                        <li>Requests made more than 30 days after purchase</li>
                    </ul>
                </section>

                <section style={styles.section}>
                    <h2 style={styles.heading}>4. How to Request a Refund</h2>
                    <p style={styles.paragraph}>
                        To request a refund, please email us at <a href="mailto:admin@apiloadtest.com" style={styles.link}>admin@apiloadtest.com</a> with:
                    </p>
                    <ul style={styles.list}>
                        <li>Your account email address</li>
                        <li>The credit pack purchased and purchase date</li>
                        <li>Reason for the refund request</li>
                    </ul>
                    <p style={styles.paragraph}>
                        We aim to respond to all refund requests within 3 business days.
                    </p>
                </section>

                <section style={styles.section}>
                    <h2 style={styles.heading}>5. Refund Processing</h2>
                    <p style={styles.paragraph}>
                        Approved refunds will be processed through the original payment method (via Dodo Payments). Refunds typically take 5–10 business days to appear on your statement, depending on your financial institution.
                    </p>
                </section>

                <section style={styles.section}>
                    <h2 style={styles.heading}>6. Credit Restoration</h2>
                    <p style={styles.paragraph}>
                        In cases where a refund is not applicable but a technical issue occurred, we may restore credits to your account instead of issuing a monetary refund.
                    </p>
                </section>

                <section style={styles.section}>
                    <h2 style={styles.heading}>7. Contact</h2>
                    <p style={styles.paragraph}>
                        For any questions about this Refund Policy, please contact us at: <a href="mailto:admin@apiloadtest.com" style={styles.link}>admin@apiloadtest.com</a>
                    </p>
                </section>
            </main>

            {/* Footer */}
            <footer style={styles.footer}>
                <div style={styles.footerContent}>
                    <div style={styles.footerLinks}>
                        <Link to="/terms" style={styles.footerLink}>Terms of Service</Link>
                        <Link to="/privacy" style={styles.footerLink}>Privacy Policy</Link>
                        <Link to="/refund" style={styles.footerLink}>Refund Policy</Link>
                        <a href="mailto:admin@apiloadtest.com" style={styles.footerLink}>Contact</a>
                    </div>
                    <p style={styles.copyright}>© 2026 APILoadTest.com. All rights reserved.</p>
                </div>
            </footer>
        </div>
    );
};

const styles = {
    page: {
        minHeight: '100vh',
        background: 'var(--light-gray)',
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        background: 'var(--primary-white)',
        padding: '16px 0',
        borderBottom: '1px solid var(--border-color)',
    },
    headerContainer: {
        maxWidth: '800px',
        margin: '0 auto',
        padding: '0 24px',
    },
    logoSection: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        textDecoration: 'none',
    },
    logoImage: { height: '40px', width: 'auto' },
    logoText: {
        fontFamily: "'Agrandir Narrow', 'Agrandir', Arial, sans-serif",
        fontSize: '24px',
        fontWeight: '700',
        color: 'var(--text-primary)',
    },
    content: {
        maxWidth: '800px',
        margin: '0 auto',
        padding: '48px 24px 80px',
        flex: 1,
    },
    title: {
        fontFamily: "'Agrandir Narrow', 'Agrandir', Arial, sans-serif",
        fontSize: '42px',
        fontWeight: '700',
        color: 'var(--text-primary)',
        marginBottom: '8px',
    },
    effectiveDate: {
        fontSize: '14px',
        color: 'var(--text-secondary)',
        marginBottom: '48px',
    },
    section: { marginBottom: '36px' },
    heading: {
        fontSize: '22px',
        fontWeight: '700',
        color: 'var(--text-primary)',
        marginBottom: '12px',
    },
    subheading: {
        fontSize: '18px',
        fontWeight: '600',
        color: 'var(--text-primary)',
        marginBottom: '8px',
        marginTop: '16px',
    },
    paragraph: {
        fontSize: '16px',
        lineHeight: '1.8',
        color: 'var(--text-primary)',
        marginBottom: '12px',
    },
    list: {
        paddingLeft: '24px',
        fontSize: '16px',
        lineHeight: '1.8',
        color: 'var(--text-primary)',
        marginBottom: '12px',
    },
    link: { color: '#6366f1', textDecoration: 'none', fontWeight: '600' },
    footer: {
        background: 'var(--primary-white)',
        padding: '32px 24px',
        borderTop: '1px solid var(--border-color)',
    },
    footerContent: { maxWidth: '800px', margin: '0 auto', textAlign: 'center' },
    footerLinks: {
        display: 'flex',
        gap: '24px',
        justifyContent: 'center',
        marginBottom: '16px',
        flexWrap: 'wrap',
    },
    footerLink: {
        color: 'var(--text-secondary)',
        textDecoration: 'none',
        fontSize: '14px',
    },
    copyright: { fontSize: '14px', color: 'var(--text-secondary)' },
};

export default RefundPolicy;
